/**
 * chatService — fonte única do estado das mensagens internas.
 *
 * POR QUE UM SERVIÇO E NÃO A NAVBAR?
 * A NavBar é importada por cada tela. Ela nasce e morre a cada troca de rota.
 * Se o polling ficasse nela, reiniciaria a cada navegação e o popup piscaria.
 * Este serviço vive uma vez só (singleton), igual a um "rádio" ligado na
 * recepção: as telas mudam, o rádio continua tocando.
 *
 * POR QUE Vue.observable E NÃO VUEX?
 * Não altera o store existente. O estado continua reativo para os componentes.
 * Se depois quiserem migrar para um módulo Vuex, a API pública não muda.
 */
import Vue from "vue";

import api from "./api";
import store from "../store";
import { userData } from "../lib/functions";

// ── Configuração ─────────────────────────────────────────────────────────
// Estimativa inicial. Ajuste conforme o número de usuários simultâneos.
const POLL_VISIBLE_MS = 20 * 1000; // aba em uso
const POLL_HIDDEN_MS = 60 * 1000; // aba em segundo plano
const SNOOZE_MS = 5 * 60 * 1000; // "Lembrar depois" = 5 minutos
const SOUND_STORAGE_KEY = "sistemaclin_chat_sound";
const RECIPIENTS_CACHE_MS = 5 * 60 * 1000; // lista de usuários muda pouco
export const BODY_MAX_LENGTH = 2000; // igual ao limite do backend

// ── Estado reativo ───────────────────────────────────────────────────────
const state = Vue.observable({
    messages: [], // não lidas, da mais antiga para a mais nova
    popupOpen: false, // popup visível?
    manualOpen: false, // aberto pelo clique no sininho (não fecha sozinho)
    snoozedUntil: 0, // timestamp até quando o popup fica adiado
    marking: false, // requisição de "marcar como lida" em andamento
    soundEnabled: readSoundPreference(),
    running: false,
    // Sobe a cada mudança (chegou, leu, enviou). Telas como o histórico
    // observam este número para se atualizar sozinhas.
    version: 0,

    // ── Compositor (Parte 3) ──
    composer: {
        open: false,
        replyTo: null, // mensagem que está sendo respondida (ou null)
        // O rascunho vive no serviço: fechar o modal por engano não perde o texto
        draft: {
            mode: "PEOPLE", // "PEOPLE" | "ALL"
            recipientIds: [],
            body: "",
            origin: "TEXT", // "TEXT" | "VOICE"
        },
    },
    recipients: [],
    recipientsLoading: false,
    sending: false,
});

let recipientsLoadedAt = 0;

// ── Controle interno (não reativo) ───────────────────────────────────────
let timer = null;
let knownIds = new Set(); // ids já vistos, para detectar o que é NOVO
let originalTitle = null;
let titleTimer = null;

// ── Utilitários ──────────────────────────────────────────────────────────
function readSoundPreference() {
    try {
        return localStorage.getItem(SOUND_STORAGE_KEY) !== "off";
    } catch {
        return true;
    }
}

function canRun() {
    let user = null;
    try {
        user = (userData() || {}).user;
    } catch {
        user = null;
    }

    if (!user) return false; // deslogado ou sessão expirada (onIdle)
    if (user.dashboard_monitoring == 1) return false; // painel de TV não lê mensagens
    if (store && store.state && store.state.online === false) return false; // offline

    return true;
}

/**
 * Bipe curto gerado pelo navegador. Não precisa de arquivo de áudio.
 * Navegadores bloqueiam som antes do primeiro clique do usuário na página.
 * Nesse caso o erro é ignorado e o alerta visual continua funcionando.
 */
function playBeep() {
    if (!state.soundEnabled) return;
    try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = 880;
        gain.gain.setValueAtTime(0.0001, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + 0.4);
        osc.onended = () => ctx.close();
    } catch {
        // som é complemento, nunca o único sinal
    }
}

/**
 * Pisca o título da aba quando ela está em segundo plano.
 * O operador vê o aviso mesmo com outra aba ou programa aberto.
 */
function startTitleBlink() {
    if (titleTimer || !document.hidden) return;
    originalTitle = document.title;
    let on = false;
    titleTimer = setInterval(() => {
        const total = state.messages.length;
        document.title = on
            ? originalTitle
            : `(${total}) ${total === 1 ? "Nova mensagem" : "Novas mensagens"}`;
        on = !on;
    }, 1500);
}

function stopTitleBlink() {
    if (titleTimer) clearInterval(titleTimer);
    titleTimer = null;
    if (originalTitle !== null) document.title = originalTitle;
    originalTitle = null;
}

function scheduleNext() {
    clearTimeout(timer);
    if (!state.running) return;
    timer = setTimeout(tick, document.hidden ? POLL_HIDDEN_MS : POLL_VISIBLE_MS);
}

async function tick() {
    if (!canRun()) {
        stop();
        return;
    }
    await fetchUnread();
    scheduleNext();
}

function onVisibilityChange() {
    if (!document.hidden) {
        stopTitleBlink();
        // Voltou para a aba: busca na hora, sem esperar o próximo ciclo.
        if (state.running) {
            clearTimeout(timer);
            tick();
        }
    }
}

// ── API pública ──────────────────────────────────────────────────────────
async function fetchUnread() {
    try {
        const response = await api.get("/rest-home/chat/unread");
        const { error, messages } = response.data || {};
        if (error || !Array.isArray(messages)) return;

        const newOnes = messages.filter((m) => !knownIds.has(m.id));
        messages.forEach((m) => knownIds.add(m.id));

        state.messages = messages;

        if (messages.length === 0) {
            // Lidas em outra aba: fecha. Aberto pelo usuário no sininho: mantém.
            if (!state.manualOpen) state.popupOpen = false;
            stopTitleBlink();
            return;
        }

        if (newOnes.length > 0) {
            state.version++;
            // Mensagem NOVA cancela o "lembrar depois": o assunto mudou.
            state.snoozedUntil = 0;
            state.popupOpen = true;
            playBeep();
            startTitleBlink();
        } else if (!state.popupOpen && Date.now() >= state.snoozedUntil) {
            // Fim do adiamento: o popup volta.
            state.popupOpen = true;
        }
    } catch (error) {
        // Falha de rede não deve quebrar a tela atual.
        console.log("chatService.fetchUnread:", error);
    }
}

async function markAsRead(ids) {
    const message_ids = (ids || state.messages.map((m) => m.id)).filter(Boolean);
    if (message_ids.length === 0 || state.marking) return false;

    try {
        state.marking = true;
        const response = await api.patch("/rest-home/chat/read", { message_ids });
        const { error } = response.data || {};
        if (error) return false;

        state.messages = state.messages.filter((m) => !message_ids.includes(m.id));
        state.version++;
        if (state.messages.length === 0) {
            state.popupOpen = false;
            state.manualOpen = false;
            stopTitleBlink();
        }
        return true;
    } catch (error) {
        console.log("chatService.markAsRead:", error);
        return false;
    } finally {
        state.marking = false;
    }
}

function snooze() {
    // Sem mensagens pendentes, "fechar" não precisa adiar nada.
    state.snoozedUntil = state.messages.length ? Date.now() + SNOOZE_MS : 0;
    state.popupOpen = false;
    state.manualOpen = false;
}

function openPopup() {
    state.snoozedUntil = 0;
    state.popupOpen = true;
    state.manualOpen = true;
}

function setSound(enabled) {
    state.soundEnabled = !!enabled;
    try {
        localStorage.setItem(SOUND_STORAGE_KEY, enabled ? "on" : "off");
    } catch {
        // modo privado: preferência vale só nesta sessão
    }
}

function start() {
    if (state.running || !canRun()) return;
    state.running = true;
    document.addEventListener("visibilitychange", onVisibilityChange);
    tick();
}

function stop() {
    state.running = false;
    clearTimeout(timer);
    timer = null;
    document.removeEventListener("visibilitychange", onVisibilityChange);
    stopTitleBlink();
    state.messages = [];
    state.popupOpen = false;
    state.manualOpen = false;
    knownIds = new Set();
    // Logout ou troca de usuário: rascunho e lista não podem vazar
    state.composer.open = false;
    clearDraft();
    state.recipients = [];
    recipientsLoadedAt = 0;
}

// ── Compositor ───────────────────────────────────────────────────────────
function emptyDraft() {
    return { mode: "PEOPLE", recipientIds: [], body: "", origin: "TEXT" };
}

/**
 * Abre o compositor.
 * - Sem argumentos: mantém o rascunho anterior (se houver).
 * - Com replyTo: pré-seleciona o remetente da mensagem respondida.
 * - Com preset: { recipientIds, body } — usado por "Cobrar quem não leu".
 */
function openComposer({ replyTo = null, preset = null } = {}) {
    const draft = state.composer.draft;

    if (preset) {
        state.composer.draft = { ...emptyDraft(), ...preset, mode: "PEOPLE" };
        replyTo = null;
    } else if (replyTo) {
        const isSameReply =
            state.composer.replyTo && state.composer.replyTo.id === replyTo.id;
        if (!isSameReply) {
            state.composer.draft = {
                ...emptyDraft(),
                recipientIds: [replyTo.sender_id],
            };
        }
    } else if (state.composer.replyTo) {
        // Era uma resposta e agora é mensagem nova: começa limpo
        state.composer.draft = emptyDraft();
    } else if (!draft) {
        state.composer.draft = emptyDraft();
    }

    state.composer.replyTo = replyTo;
    state.composer.open = true;
    fetchRecipients();
}

function closeComposer() {
    state.composer.open = false;
}

function clearDraft() {
    state.composer.draft = emptyDraft();
    state.composer.replyTo = null;
}

async function fetchRecipients({ force = false } = {}) {
    const fresh = Date.now() - recipientsLoadedAt < RECIPIENTS_CACHE_MS;
    if ((!force && fresh && state.recipients.length) || state.recipientsLoading)
        return;

    try {
        state.recipientsLoading = true;
        const response = await api.get("/rest-home/chat/recipients");
        const { error, users } = response.data || {};
        if (!error && Array.isArray(users)) {
            state.recipients = users;
            recipientsLoadedAt = Date.now();
        }
    } catch (error) {
        console.log("chatService.fetchRecipients:", error);
    } finally {
        state.recipientsLoading = false;
    }
}

/**
 * Envia o rascunho atual.
 * Retorna { ok, errors, message } para o componente exibir o resultado.
 */
async function sendDraft() {
    const { draft, replyTo } = state.composer;
    if (state.sending) return { ok: false, errors: [] };

    const payload = {
        body: (draft.body || "").trim(),
        origin: draft.origin,
        to_all: draft.mode === "ALL",
        recipient_ids: draft.mode === "ALL" ? [] : draft.recipientIds,
    };

    try {
        state.sending = true;
        const response = await api.post("/rest-home/chat/message", payload);
        const { error, errors, message } = response.data || {};

        if (error) return { ok: false, errors: errors || ["NÃO FOI POSSÍVEL ENVIAR"] };

        // Responder implica ter lido: remove a mensagem original da fila
        if (replyTo && state.messages.some((m) => m.id === replyTo.id)) {
            await markAsRead([replyTo.id]);
        }

        clearDraft();
        closeComposer();
        state.version++;
        return { ok: true, errors: [], message };
    } catch (error) {
        console.log("chatService.sendDraft:", error);
        return {
            ok: false,
            errors: ["FALHA DE CONEXÃO. SUA MENSAGEM FOI MANTIDA, TENTE NOVAMENTE."],
        };
    } finally {
        state.sending = false;
    }
}

/** Chamado a cada troca de rota: liga se pode, desliga se não pode. */
function sync() {
    if (canRun()) start();
    else stop(); // idempotente: garante limpeza de rascunho mesmo se já parado
}

export default {
    state,
    start,
    stop,
    sync,
    fetchUnread,
    markAsRead,
    snooze,
    openPopup,
    setSound,
    openComposer,
    closeComposer,
    clearDraft,
    fetchRecipients,
    sendDraft,
};